import type { CatalogProductImage } from "@shared/types";
import { Button } from "@shared-ui/button";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { ProductMedia } from "@/features/catalog/components/product-media";
import { sameImages } from "@/features/catalog/lib/images";
import {
	type AdminVariant,
	createVariant,
	updateVariant,
	type VariantInput,
} from "@/features/catalog/services/catalog-api.service";
import { apiErrorMessage } from "@/lib/api-error";

/**
 * Create or edit one variant.
 *
 * THE FORM IS GENERATED FROM THE CATEGORY'S DECLARED AXES, the same way the
 * Specifications tab is generated from its attributes. A category that varies by
 * finish and size gets two inputs named Finish and Size; nothing here knows what
 * either word means, and plan 008 V2 greps for exactly that.
 *
 * A DECLARED AXIS IS WRITTEN TO THE TYPED COLUMNS, never to
 * `variantAttributes`. The schema calls `variantAxis1Name/Value` and
 * `variantAxis2Name/Value` "what the UI filters on" and keeps the bag for
 * everything that is not a browse axis -- so a declared axis written into the
 * bag produces a variant no browse filter can see. That is also why the typed
 * pair caps a category at two browse axes: a template declaring a third is a
 * refusal here rather than an overflow into the bag, because the overflow would
 * be silent and the refusal is not.
 *
 * REQUIREDNESS IS NOT ENFORCED, and this form must not imply that it is. Nothing
 * in the system checks for a missing axis: `validateVariantAttributes` skips a
 * key with no matching definition and never looks for an absent one,
 * `VariantAttributeDef` carries no `isRequired`, and the typed axes are checked
 * only for PAIRING. A variant may therefore carry one of two declared axes, and
 * the server will accept it. Marking these fields required would be a validation
 * the server does not perform.
 *
 * What IS enforced, and surfaced as a field-level message rather than a generic
 * failure: `assertVariantAxesRemainPaired` refuses a name without a value or a
 * value without a name, because a named axis with no value cannot be filtered on.
 */

const TITLE_ID = "variant-dialog-title";

/** The two browse axes the schema has columns for. */
export const MAX_BROWSE_AXES = 2;

export interface DeclaredAxis {
	name: string;
	type: string;
	enumValues?: string[];
}

export type VariantDialogMode =
	| { kind: "create" }
	| { kind: "edit"; variant: AdminVariant };

interface VariantDialogProps {
	productId: string;
	mode: VariantDialogMode;
	/** The category's declared variant axes, in template order. */
	axes: DeclaredAxis[];
	onClose: () => void;
	onSaved: () => Promise<void> | void;
}

/** What an existing variant holds for a declared axis, by name. */
function storedAxisValue(
	variant: AdminVariant | null,
	axisName: string,
): string {
	if (!variant) return "";
	if (variant.variantAxis1Name === axisName) {
		return variant.variantAxis1Value ?? "";
	}
	if (variant.variantAxis2Name === axisName) {
		return variant.variantAxis2Value ?? "";
	}
	return "";
}

/** The column pairs, as slot numbers. */
const SLOTS = [1, 2] as const;
type Slot = (typeof SLOTS)[number];

function nameKeyFor(slot: Slot): "variantAxis1Name" | "variantAxis2Name" {
	return slot === 1 ? "variantAxis1Name" : "variantAxis2Name";
}

function valueKeyFor(slot: Slot): "variantAxis1Value" | "variantAxis2Value" {
	return slot === 1 ? "variantAxis1Value" : "variantAxis2Value";
}

/**
 * Which column pair each rendered axis writes to, and which pairs this form
 * does not own.
 *
 * BY STORED NAME FIRST, not by position. A purely positional assignment --
 * declared axis 1 to slot 1, declared axis 2 to slot 2 -- is right on create and
 * wrong on edit, because the template's axis list moves and the stored columns
 * do not. Drop a template's first axis and the second one shifts to position 1,
 * so a positional write puts it into the pair the first axis still occupies and
 * the variant ends up carrying the same axis twice.
 *
 * AND A PAIR NO RENDERED AXIS OWNS IS LEFT ALONE. This is the half that matters:
 * a variant can hold an axis the template no longer declares (removed, renamed,
 * or pushed past the two the columns can carry), the form has no field for it,
 * and the edit patch used to compute `null` for it and send that -- so opening
 * Edit to fix a SKU and pressing Save deleted an axis value the operator was
 * never shown, with the server's pairing guard waving it through because null
 * and null is a whole pair. `droppedAxes` in the impact preview exists to warn
 * about losing exactly this data; it should not then be lost by an unrelated edit.
 */
function resolveSlots(
	usable: DeclaredAxis[],
	editing: AdminVariant | null,
): {
	owned: Map<Slot, string>;
	preserved: Array<{ slot: Slot; name: string }>;
} {
	const owned = new Map<Slot, string>();
	const claimed = new Set<string>();

	// An axis already stored keeps the pair it is already in.
	for (const slot of SLOTS) {
		const storedName = editing?.[nameKeyFor(slot)] ?? null;
		if (storedName && usable.some((axis) => axis.name === storedName)) {
			owned.set(slot, storedName);
			claimed.add(storedName);
		}
	}

	// A pair holding a name no rendered axis matches is not this form's to write.
	const preserved: Array<{ slot: Slot; name: string }> = [];
	for (const slot of SLOTS) {
		if (owned.has(slot)) continue;
		const storedName = editing?.[nameKeyFor(slot)] ?? null;
		if (storedName) preserved.push({ slot, name: storedName });
	}

	// Whatever is left over goes into whatever is free, in template order.
	const free = SLOTS.filter(
		(slot) =>
			!owned.has(slot) && !preserved.some((entry) => entry.slot === slot),
	);
	for (const axis of usable) {
		if (claimed.has(axis.name)) continue;
		const slot = free.shift();
		if (slot === undefined) break;
		owned.set(slot, axis.name);
	}

	return { owned, preserved };
}

export function VariantDialog({
	productId,
	mode,
	axes,
	onClose,
	onSaved,
}: VariantDialogProps) {
	const editing = mode.kind === "edit" ? mode.variant : null;

	/*
	 * At most two, and the extras are NAMED rather than dropped.
	 *
	 * A template declaring three browse axes cannot be represented -- there are
	 * two column pairs. Silently writing the first two would produce variants
	 * whose third axis exists in the template and nowhere in the data, which is
	 * the kind of gap nobody finds until a filter returns nothing.
	 */
	const usable = axes.slice(0, MAX_BROWSE_AXES);
	const overflow = axes.slice(MAX_BROWSE_AXES);

	/*
	 * A VARIANT'S OWN IMAGES, through the SAME manager a product uses.
	 *
	 * `product_variants.images` has always existed; what was missing was any
	 * control that wrote it, which is why plan 008's M5 could not be run on a
	 * variant -- only on a product. Reusing `ProductMedia` rather than building a
	 * second manager is what keeps alt text, the type selector, positional
	 * primary and the upload policy identical on both; two copies of this screen
	 * would drift, and the one nobody is looking at is the one that drifts.
	 *
	 * Uploads are keyed by PRODUCT, which is right: a variant's image lives in
	 * its product's storage and the route has no variant-scoped equivalent.
	 */
	const [images, setImages] = useState<CatalogProductImage[]>(
		() => editing?.images ?? [],
	);
	const [uploading, setUploading] = useState(false);
	const [sku, setSku] = useState(editing?.sku ?? "");
	const [brandSku, setBrandSku] = useState(editing?.brandSku ?? "");
	const [values, setValues] = useState<Record<string, string>>(() =>
		Object.fromEntries(
			usable.map((axis) => [axis.name, storedAxisValue(editing, axis.name)]),
		),
	);

	const { owned, preserved } = resolveSlots(usable, editing);

	const save = useMutation({
		mutationFn: async () => {
			/*
			 * Positional: the first declared axis goes in the first column pair.
			 *
			 * An axis left blank writes NEITHER the name nor the value, which is
			 * what keeps the pair whole -- writing the name alone is precisely what
			 * `assertVariantAxesRemainPaired` refuses.
			 */
			const axisFields: Partial<VariantInput> = {};
			for (const slot of SLOTS) {
				const axisName = owned.get(slot);
				if (!axisName) continue;
				const value = values[axisName]?.trim() ?? "";
				if (value === "") continue;
				axisFields[nameKeyFor(slot)] = axisName;
				axisFields[valueKeyFor(slot)] = value;
			}

			if (mode.kind === "create") {
				await createVariant(productId, {
					sku: sku.trim(),
					...(brandSku.trim() ? { brandSku: brandSku.trim() } : {}),
					...(images.length > 0 ? { images } : {}),
					...axisFields,
				});
				return;
			}

			/*
			 * On edit an OWNED axis slot is sent EXPLICITLY, including as null when
			 * it has been cleared. An absent key leaves the stored value alone, so
			 * omitting a cleared axis would silently keep it -- and clearing only
			 * the value while leaving the name is the half-populated state the
			 * server refuses.
			 *
			 * A slot in `preserved` is skipped entirely, which is what makes an
			 * undeclared axis survive an edit rather than being nulled by it. See
			 * `resolveSlots`.
			 */
			const patch: Record<string, unknown> = {};
			if (sku.trim() !== editing?.sku) patch.sku = sku.trim();
			/*
			 * Sent only when it actually moved, and compared on EVERY field rather
			 * than on the URLs: an operator who only edited alt text changed
			 * nothing about the URL list, and a URL-only comparison would decide
			 * nothing had changed and drop their description.
			 */
			if (!sameImages(images, editing?.images ?? [])) patch.images = images;
			if (brandSku.trim() !== (editing?.brandSku ?? "")) {
				patch.brandSku = brandSku.trim() === "" ? null : brandSku.trim();
			}
			for (const slot of SLOTS) {
				if (preserved.some((entry) => entry.slot === slot)) continue;
				const nameKey = nameKeyFor(slot);
				const valueKey = valueKeyFor(slot);
				const nextName = axisFields[nameKey] ?? null;
				const nextValue = axisFields[valueKey] ?? null;
				if (nextName !== (editing?.[nameKey] ?? null))
					patch[nameKey] = nextName;
				if (nextValue !== (editing?.[valueKey] ?? null)) {
					patch[valueKey] = nextValue;
				}
			}

			if (Object.keys(patch).length === 0) return;
			await updateVariant(editing?.id ?? "", patch as Partial<VariantInput>);
		},
		onSuccess: async () => {
			await onSaved();
			onClose();
		},
	});

	/*
	 * `uploading` blocks Save for the same reason the product editor does: a save
	 * landing between an upload starting and its URL arriving writes a list that
	 * is missing the image the operator just picked.
	 */
	const canSave = sku.trim() !== "" && !save.isPending && !uploading;

	return (
		<Dialog
			open
			titleId={TITLE_ID}
			onClose={() => {
				/*
				 * `uploading` too, not just `save.isPending`.
				 *
				 * Escape used to close the dialog while a file was still in flight.
				 * The upload keeps going, its URL lands in a component that has
				 * unmounted, and the file is orphaned in storage with nothing
				 * referencing it -- an exit that looks like a cancel and is not one.
				 * Save was already gated on this; the two exits have to agree.
				 */
				if (!save.isPending && !uploading) onClose();
			}}
			className="grid max-h-[90vh] w-full max-w-lg gap-4 overflow-y-auto rounded-3xl border bg-white p-5 shadow-xl"
		>
			<div>
				<h2 id={TITLE_ID} className="text-lg font-semibold">
					{editing ? `Edit ${editing.sku}` : "Add a variant"}
				</h2>
				<p className="text-sm text-muted-foreground">
					A variant is the same product in a different finish, size or colour.
					It carries its own SKU. Its price is recorded on the product&apos;s
					Pricing tab.
				</p>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="variant-sku">SKU</Label>
				<Input
					id="variant-sku"
					value={sku}
					onChange={(event) => setSku(event.target.value)}
				/>
				<p className="text-xs text-muted-foreground">
					Unique across every live variant, not just this product's. A retired
					variant's SKU is free to use again.
				</p>
			</div>

			{usable.map((axis) => (
				<div className="grid gap-1.5" key={axis.name}>
					<Label htmlFor={`variant-axis-${axis.name}`}>{axis.name}</Label>
					{axis.type === "enum" && axis.enumValues?.length ? (
						<Select
							value={values[axis.name] ?? ""}
							onValueChange={(next) =>
								setValues((current) => ({ ...current, [axis.name]: next }))
							}
						>
							<SelectTrigger id={`variant-axis-${axis.name}`}>
								<SelectValue placeholder={`Choose a ${axis.name}`} />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									{axis.enumValues.map((option) => (
										<SelectItem key={option} value={option}>
											{option}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
					) : (
						<Input
							id={`variant-axis-${axis.name}`}
							type={axis.type === "number" ? "number" : "text"}
							value={values[axis.name] ?? ""}
							onChange={(event) =>
								setValues((current) => ({
									...current,
									[axis.name]: event.target.value,
								}))
							}
						/>
					)}
				</div>
			))}

			{usable.length > 0 ? (
				// Said plainly, because the form gives no other clue and the server
				// genuinely accepts a partial set. An operator who assumes otherwise
				// invents values to fill the boxes.
				<p className="text-xs text-muted-foreground">
					Leave an axis blank if this variant does not vary along it. Nothing
					requires every axis to be filled.
				</p>
			) : null}

			{preserved.length > 0 ? (
				/*
				 * Named, because the alternative to showing it is an operator saving
				 * a variant while a value they cannot see rides along unmentioned.
				 * Read-only: the template does not declare this axis, so there is no
				 * field to offer and no valid value to offer for it.
				 */
				<div className="grid gap-1 rounded-2xl border border-dashed p-3 text-xs">
					<p className="font-medium">Kept as it is</p>
					{preserved.map((entry) => (
						<p className="text-muted-foreground" key={entry.name}>
							{entry.name}: {editing?.[valueKeyFor(entry.slot)] ?? ""} — this
							category's template no longer declares {entry.name}, so it is not
							editable here and saving will leave it untouched.
						</p>
					))}
				</div>
			) : null}

			{overflow.length > 0 ? (
				<p role="alert" className="text-sm text-amber-800">
					This category declares {axes.length} variant axes and a variant can
					carry {MAX_BROWSE_AXES}.{" "}
					{overflow.map((one) => one.name).join(" and ")}{" "}
					{overflow.length === 1 ? "is" : "are"} not offered here. Remove{" "}
					{overflow.length === 1 ? "it" : "them"} from the category's template,
					or record {overflow.length === 1 ? "it" : "them"} as a specification
					instead.
				</p>
			) : null}

			<div className="grid gap-1.5">
				<Label htmlFor="variant-brand-sku">Brand SKU (optional)</Label>
				<Input
					id="variant-brand-sku"
					value={brandSku}
					onChange={(event) => setBrandSku(event.target.value)}
				/>
			</div>

			<div className="grid gap-1.5">
				<Label>Images (optional)</Label>
				{/*
				 * THE SAME MANAGER A PRODUCT USES, not a second one. Alt text,
				 * the type selector, positional primary and the upload policy
				 * are identical here because it is literally the same component
				 * -- which is the duplication plan 008's primitives work set out
				 * to stop.
				 */}
				<ProductMedia
					productId={productId}
					images={images}
					onChange={setImages}
					onUploadingChange={setUploading}
					disabled={save.isPending}
				/>
				<p className="text-xs text-muted-foreground">
					Leave empty to show the product's own images for this variant.
				</p>
			</div>

			{save.isError ? (
				/*
				 * The server's own sentence. Two refusals reach here and both name
				 * what to do: a duplicate SKU names the product already holding it,
				 * and the pairing rule names which half of which axis is missing.
				 */
				<p role="alert" className="text-sm text-red-700">
					{apiErrorMessage(save.error, "Could not save this variant.")}
				</p>
			) : null}

			<div className="flex justify-end gap-2 border-t pt-3">
				<Button
					variant="ghost"
					onClick={onClose}
					disabled={save.isPending || uploading}
				>
					Cancel
				</Button>
				<Button onClick={() => save.mutate()} disabled={!canSave}>
					{save.isPending ? "Saving…" : "Save"}
				</Button>
			</div>
		</Dialog>
	);
}
